<template>
  <div class="container">
    <LoadingSpinner :loading="loading"></LoadingSpinner>

    <div class="mt-4" style="max-width:545px">
      <h3>Kjøkkentid</h3>
      <p>Hvor raskt kjøkkenet lager bestillingene, og om de blir klare til tiden gjesten fikk. Tallene kommer fra kjøkkenskjermen: når en bestilling trykkes Ferdig, og når den hentes.</p>

      <!-- Reached by its address: the menu offers the report only where there is a KDS. -->
      <div v-if="loaded && locations.length === 0" class="kt-empty mt-4">
        Kjøkkentiden måles av kjøkkenskjermen (KDS), og dere har ingen kjøkkenskjerm ennå. Rapporten fylles når en KDS
        er tatt i bruk.
      </div>

      <template v-else>
        <!-- SUPER_ADMIN is offered every location, so it is told what the merchant does not see. -->
        <div v-if="loaded && isSuperAdmin && kdsLocationIds && kdsLocationIds.size === 0" class="kt-empty mt-4">
          Merchanten har ingen kjøkkenskjerm (KDS). Rapporten vises bare for SUPER_ADMIN, og den blir tom til en KDS er
          tatt i bruk.
        </div>
        <!-- A group with a KDS at only one of its restaurants: say which one the report is for. -->
        <div v-if="locations.length === 1 && allLocationsCount > 1" class="mt-4">
          <span class="ingredient-title">Avdeling</span> {{ locations[0].name }}
        </div>
        <div v-if="locations.length > 1" class="mt-4">
          <span class="ingredient-title">Velg avdeling</span>
          <select class="form-select" v-model="locationId">
            <option v-for="location in locations" :key="location.id" :value="location.id">{{ locationLabel(location) }}</option>
          </select>
        </div>

        <SelectDate ref="selectDateFrom" label="Velg fra og med dato" :selectedDate="fromDateString"/>
        <SelectDate ref="selectDateTo" label="Velg til og med dato" :selectedDate="toDateString"/>

        <button type="button" class="bo_btn mt-3 mb-4" @click="showReport">Vis kjøkkentid</button>
        <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>
      </template>
    </div>

    <div v-if="report" class="mb-5">
      <div v-if="report.orders === 0" class="kt-empty">
        Ingen bestillinger med kjøkkentid i perioden. Tallene kommer fra kjøkkenskjermen.
      </div>

      <template v-else>
        <div class="row g-3">
          <div class="col-md-4">
            <div class="kt-card">
              <div class="kt-card__label">Kjøkkentid i snitt</div>
              <div class="kt-card__value">{{ minutes(report.avgKitchenMinutes) }}</div>
              <div class="kt-card__note">
                Median {{ minutes(report.medianKitchenMinutes) }} · 90 % innen {{ minutes(report.p90KitchenMinutes) }}
                · {{ report.timedOrders }} bestillinger
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="kt-card">
              <div class="kt-card__label">Klar til lovet tid</div>
              <div class="kt-card__value">{{ onTimeShare }}</div>
              <div class="kt-card__note">
                {{ report.lateOrders }} av {{ report.promisedOrders }} for sent<span v-if="report.lateOrders > 0">, i snitt {{ minutes(report.avgLateMinutes) }}</span>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="kt-card">
              <div class="kt-card__label">Venter ved disken</div>
              <div class="kt-card__value">{{ minutes(report.avgPickupWaitMinutes) }}</div>
              <div class="kt-card__note">Fra klar til hentet · {{ report.pickedUpOrders }} bestillinger</div>
            </div>
          </div>
        </div>

        <div class="kt-chart mt-4">
          <h5>Kjøkkentid per time</h5>
          <apexchart type="bar" height="280" :options="chartOptions(hourLabels, 'min')" :series="[{ name: 'Snitt', data: hourValues }]" />
        </div>
        <div class="kt-chart mt-4">
          <h5>Kjøkkentid per ukedag</h5>
          <apexchart type="bar" height="280" :options="chartOptions(weekdayLabels, 'min')" :series="[{ name: 'Snitt', data: weekdayValues }]" />
        </div>
        <div v-if="report.byDay.length > 1" class="kt-chart mt-4">
          <h5>Kjøkkentid per dag</h5>
          <apexchart type="line" height="280" :options="chartOptions(dayLabels, 'min')" :series="[{ name: 'Snitt', data: dayValues }]" />
        </div>

        <div v-if="report.byStation.length > 0" class="mt-4" style="max-width:545px">
          <h5>Per stasjon</h5>
          <table class="table">
            <thead><tr><th>Stasjon</th><th>Bestillinger</th><th>Ferdig etter (snitt)</th></tr></thead>
            <tbody>
              <tr v-for="station in report.byStation" :key="station.name">
                <td>{{ station.name }}</td><td>{{ station.orders }}</td><td>{{ minutes(station.avgMinutes) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="bo_form-hint mt-4" style="max-width:545px">
          Kjøkkentid er fra bestillingen kom til kjøkkenet trykket Ferdig. Forhåndsbestillinger — lovet mer enn
          90 minutter etter at de kom — teller ikke i kjøkkentiden, men teller i om de ble klare til tiden.
          Per stasjon er fra bestillingen kom til stasjonen var ferdig.
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import SelectDate from "@/components/other/SelectDate";
import ReportService from "@/services/report/ReportService";
import TerminalService from "@/services/terminal/TerminalService";
import TerminalVisibility from "@/util/TerminalVisibility";
import UserUtil from "@/util/UserUtil";
import VueApexCharts from 'vue3-apexcharts';
import moment from "moment";

const WEEKDAYS = ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'];

/**
 * Speed of service (TastyKDS roadmap phase 3, item 16), from the timestamps the kitchen display writes on each order.
 * The arithmetic — and what counts — is CagistaServices kitchen/KitchenTimesReport.
 */
export default {
  components: {LoadingSpinner, SelectDate, apexchart: VueApexCharts},
  data() {
    return {
      loading: true,
      loaded: false,   // the locations were read — an empty list is then an answer, not a failure
      locationId: '',
      locations: [],
      allLocationsCount: 0,
      kdsLocationIds: null,   // Set of the location ids with a KDS; null when the terminals could not be read
      isSuperAdmin: UserUtil.isSuperAdmin(),
      fromDateString: moment().subtract(6, 'days').format('YYYY-MM-DD'),
      toDateString: moment().format('YYYY-MM-DD'),
      report: null,
      errorMessage: ''
    };
  },
  computed: {
    onTimeShare() {
      if (!this.report || this.report.promisedOrders === 0) return '–';
      return Math.round((this.report.promisedOrders - this.report.lateOrders) * 100 / this.report.promisedOrders) + ' %';
    },
    hourLabels() { return this.report.byHour.map(row => String(row.hour).padStart(2, '0')); },
    hourValues() { return this.report.byHour.map(row => row.avgKitchenMinutes); },
    weekdayLabels() { return this.report.byWeekday.map(row => WEEKDAYS[row.weekday - 1]); },
    weekdayValues() { return this.report.byWeekday.map(row => row.avgKitchenMinutes); },
    dayLabels() { return this.report.byDay.map(row => moment(row.date, 'YYYY-MM-DD').format('DD.MM')); },
    dayValues() { return this.report.byDay.map(row => row.avgKitchenMinutes); }
  },
  mounted() {
    // Only the locations with a KDS: elsewhere the report can only be empty. Read here rather than from the cached
    // MerchantUtil.kdsLocationIds(), which a page opened by its address may reach before it is filled. If the
    // terminals cannot be read, every location is offered — the report still answers, it is only less tidy.
    // SUPER_ADMIN is offered every location, the ones without a KDS marked, to see that the report is there.
    const kdsLocationIds = TerminalService.getTerminals().then(
      (response) => Array.isArray(response.data)
        ? new Set(TerminalVisibility.forMerchantView(response.data).filter(terminal => terminal.terminalType === 'KDS').map(terminal => terminal.locationId))
        : null,
      (error) => { console.log(error); return null; }
    );
    Promise.all([ReportService.getDataForReportGui(), kdsLocationIds]).then(
      ([response, kdsIds]) => {
        const locations = response.data.locations || [];
        this.allLocationsCount = locations.length;
        this.kdsLocationIds = kdsIds;
        this.locations = kdsIds && !this.isSuperAdmin ? locations.filter(location => kdsIds.has(location.id)) : locations;
        this.locationId = this.locations.length ? this.locations[0].id : '';
        this.loaded = true;
        this.loading = false;
      },
      (error) => { console.log(error); this.errorMessage = 'Kunne ikke hente avdelingene.'; this.loading = false; }
    );
  },
  methods: {
    locationLabel(location) {
      return this.kdsLocationIds && !this.kdsLocationIds.has(location.id) ? location.name + ' (ingen KDS)' : location.name;
    },
    minutes(value) {
      return value === null || value === undefined ? '–' : String(value).replace('.', ',') + ' min';
    },
    chartOptions(categories, unit) {
      return {
        chart: { toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#ECB109'],
        dataLabels: { enabled: false },
        xaxis: { categories: categories },
        yaxis: { labels: { formatter: (value) => value === null ? '' : Math.round(value) + ' ' + unit } },
        tooltip: { y: { formatter: (value) => value === null ? 'ingen målinger' : String(value).replace('.', ',') + ' ' + unit } }
      };
    },
    showReport() {
      this.errorMessage = '';
      this.fromDateString = this.$refs.selectDateFrom.getSelectedDate();
      this.toDateString = this.$refs.selectDateTo.getSelectedDate();
      this.loading = true;
      ReportService.getKitchenTimesReport(this.locationId, this.fromDateString, this.toDateString).then(
        (response) => {
          this.loading = false;
          if (response.data && response.data.result === 'SUCCEEDED') {
            this.report = response.data;
          } else {
            this.report = null;
            this.errorMessage = (response.data && response.data.message) || 'Kunne ikke hente rapporten.';
          }
        },
        (error) => {
          console.log(error);
          this.loading = false;
          this.report = null;
          this.errorMessage = 'Kunne ikke hente rapporten.';
        }
      );
    }
  }
};
</script>

<style scoped>
.kt-card {
  border: 1px solid var(--bo-muted-2, #ddd);
  border-radius: 8px;
  padding: 16px;
  height: 100%;
}
.kt-card__label {
  color: var(--bo-muted, #6c757d);
}
.kt-card__value {
  font-size: 2rem;
  font-weight: 700;
}
.kt-card__note {
  color: var(--bo-muted, #6c757d);
  font-size: 0.9rem;
}
.kt-chart {
  max-width: 900px;
}
.kt-empty {
  max-width: 545px;
  color: var(--bo-muted, #6c757d);
}
</style>
